import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { Loading, GridHookForm } from 'vnetwork-ui'
import {
    getEmployeeInfo,
    updateEmployeeFinance,
} from '@/modules/Employee/services'
import { validateStep } from '../../../Profile/schema'
import { EmployeeConfimationValues } from '@/modules/Employee/types'
import { FormValues } from '../../../Confirmation'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getCurrentEmployee,
    updateCurrentEmployee,
} from '@/modules/Account/components/services'

interface FinanceProps {
    employeeId?: number
    isSelf?: boolean
}

const Finance: React.FC<FinanceProps> = ({ employeeId, isSelf = false }) => {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: isSelf
            ? [QUERY_KEYS.CURRENT_USER_PROFILE]
            : [QUERY_KEYS.EMPLOYEE, id],
        queryFn: () => (isSelf ? getCurrentEmployee() : getEmployeeInfo(id)),
        enabled: isSelf || !!id,
        loadingComponent: (
            <div className="flex justify-center items-center min-h-[200px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const { mutateAsync: updateEmployeeMutation, isPending: isUpdatePending } =
        useMutationWithHandler(
            {
                mutationFn: (isSelf
                    ? updateCurrentEmployee
                    : updateEmployeeFinance) as any,
            },
            {
                reactQueryKeys: isSelf
                    ? QUERY_KEYS.CURRENT_USER_PROFILE
                    : QUERY_KEYS.EMPLOYEES,
                successMessage: 'employee-update-success',
                errorMessage: 'error-occurred',
            }
        )

    const formControl = useForm<any>({
        defaultValues: {
            employee_bank_name:
                data?.result?.employee_bank_name || 'Techcombank',
            employee_bank_branch: data?.result?.employee_bank_branch || '',
            employee_bank_number: data?.result?.employee_bank_number || '',
            employee_personal_tax_code:
                data?.result?.employee_personal_tax_code || '',
            employee_health_insurance:
                data?.result?.employee_health_insurance || '',
            employee_social_insurance:
                data?.result?.employee_social_insurance || '',
            employee_medical_registration_place:
                data?.result?.employee_medical_registration_place || '',
        },
        resolver: yupResolver(validateStep({ activeTab: 'Finance' }) as any),
    })

    useEffect(() => {
        formControl.reset({
            employee_bank_name:
                data?.result?.employee_bank_name || 'Techcombank',
            employee_bank_branch: data?.result?.employee_bank_branch || '',
            employee_bank_number: data?.result?.employee_bank_number || '',
            employee_personal_tax_code:
                data?.result?.employee_personal_tax_code || '',
            employee_health_insurance:
                data?.result?.employee_health_insurance || '',
            employee_social_insurance:
                data?.result?.employee_social_insurance || '',
            employee_medical_registration_place:
                data?.result?.employee_medical_registration_place || '',
        })
    }, [data])

    const onSubmit = async (values: Partial<EmployeeConfimationValues>) => {
        if (isSelf) {
            await updateEmployeeMutation(values as any)
        } else {
            await updateEmployeeMutation({
                id,
                employee: values as Partial<FormValues>,
            } as any)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <GridHookForm
            actions={[
                {
                    title: t('save-changes'),
                    type: 'submit',
                    variant: 'contained',
                    loading: isUpdatePending,
                    disabled: isUpdatePending || !formControl.formState.isDirty,
                },
            ]}
            formControl={formControl}
            onSubmit={onSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data={[
                {
                    formItemProps: {
                        type: 'textfield',
                        name: 'employee_bank_name',
                        placeholder: t('employees.bank_name'),
                        label: t('employees.bank_name'),
                        className: 'md:col-span-2',
                    },
                },
                {
                    formItemProps: {
                        type: 'textfield',
                        name: 'employee_bank_branch',
                        placeholder: t('employees.bank_branch'),
                        label: t('employees.bank_branch'),
                    },
                },
                {
                    formItemProps: {
                        type: 'textfield',
                        name: 'employee_bank_number',
                        placeholder: t('employees.bank_number'),
                        label: t('employees.bank_number'),
                    },
                    className: 'md:col-span-2',
                },
                {
                    formItemProps: {
                        type: 'textfield',
                        name: 'employee_personal_tax_code',
                        placeholder: t('employees.personal_tax_code'),
                        label: t('employees.personal_tax_code'),
                    },
                    className: 'md:col-span-2',
                },
                {
                    formItemProps: {
                        type: 'textfield',
                        name: 'employee_health_insurance',
                        placeholder: t('health-insurance'),
                        label: t('health-insurance'),
                    },
                },
                {
                    formItemProps: {
                        type: 'textfield',
                        name: 'employee_social_insurance',
                        placeholder: t('social-insurance'),
                        label: t('social-insurance'),
                    },
                },
                {
                    formItemProps: {
                        type: 'textfield',
                        name: 'employee_medical_registration_place',
                        placeholder: t('place-to-register'),
                        label: t('place-to-register'),
                    },
                    className: 'md:col-span-2',
                },
            ]}
        />
    )
}

export default Finance
